import { useEffect, useState } from 'react';
import { listImportDrafts } from './api';
import { useRoute } from './router';

/**
 * Combien de recettes attendent au sas (US-71, sorti de `Navigation.tsx` en
 * US-72).
 *
 * ### Pourquoi ce hook a quitté la navigation
 *
 * Il y avait un seul appelant — la pastille — et c'était la bonne place. US-72
 * lui en donne un second, et pour une raison qui est un défaut réparé :
 * **la pastille annonçait un endroit qu'aucun lien n'atteignait**. Le sas ne
 * s'ouvrait qu'en tapant `/sas` dans la barre d'adresse. Le défaut vient
 * d'US-71, mais il ne pouvait pas se voir avant US-72 : rien ne remplissait le
 * sas, la pastille restait à zéro, et personne ne cherchait la porte.
 *
 * La bibliothèque porte donc désormais le lien, et elle a besoin du même
 * compte. Le recopier aurait produit deux lectures qui divergent le jour où
 * l'une gagne un cas — la pastille dirait trois et le lien deux.
 *
 * ### Ce que cela coûte
 *
 * Une seconde requête en arrivant sur la bibliothèque, sur une table qui
 * compte une poignée de lignes. La navigation, elle, relit à chaque
 * changement d'écran de toute façon. Lever l'état jusqu'à `App` pour n'en
 * faire qu'une aurait fait traverser deux composants à une valeur que personne
 * d'autre n'utilise — c'est l'abstraction anticipée que le §5 refuse.
 */
export function useSasEnAttente(modePartage: boolean): number {
  const [enAttente, setEnAttente] = useState(0);
  const route = useRoute();

  useEffect(() => {
    if (modePartage) return;
    const controller = new AbortController();
    Promise.resolve()
      .then(() => listImportDrafts(controller.signal))
      .then((sas) => { setEnAttente(Array.isArray(sas.drafts) ? sas.drafts.length : 0); })
      .catch(() => { /* un compte absent vaut mieux qu'une navigation cassée */ });
    return () => { controller.abort(); };
    // Relu à chaque changement d'écran : c'est en revenant du sas que le
    // compte change.
  }, [modePartage, route.name]);

  // **Zéro tant qu'on est sur la tablette, et pas seulement « pas relu ».**
  // `modePartage` n'est connu qu'une fois la session lue : au premier rendu il
  // vaut `false`, la lecture part, et rendre `enAttente` tel quel laisserait la
  // pastille afficher un compte obtenu avant qu'on sache où l'on est. Calculé
  // au rendu plutôt que remis à zéro dans l'effet — un `setState` synchrone
  // dans un effet enchaîne les rendus pour rien.
  //
  // **`useNouveautes` (Navigation.tsx) porte la même ligne depuis US-80.** Elle
  // ne l'avait pas : le raisonnement ci-dessus a été écrit ici, à un `import`
  // de là, et n'a pas voyagé pendant trois stories parce que rien ne le fait
  // voyager. Les deux hooks ne sont pas fusionnés — deux appels de trois lignes
  // ne justifient pas un paramètre de fonction (§5) —, ils se citent.
  return modePartage ? 0 : enAttente;
}
